import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimePrivateEvent } from '@/lib/realtime';
import { invalidateTradingCachesDebounced } from './invalidate-trading-caches';

/**
 * Invalidates the positions/orders/trades caches on private bus fill/sell/cancel
 * events, so Open Positions / My History / Portfolio stay live (including resting
 * orders that fill later or trades on another device). REST stays the source of
 * truth — the socket only invalidates. Subscribes to the events delivered over
 * direct Centrifugo today, so it works without the backend push; settlement (no
 * fill/trade) is handled by {@link useSettlementResync}. Debounced, so a single
 * buy's burst of events collapses to one refetch. Mount once per trading surface,
 * under the `PrivateEventBusProvider`. Mirrors {@link useWalletBalanceRealtime}.
 */
export function useTradingRealtimeSync(): void {
  const queryClient = useQueryClient();
  const invalidate = useCallback(() => invalidateTradingCachesDebounced(queryClient), [queryClient]);

  useRealtimePrivateEvent('order:filled', invalidate);
  useRealtimePrivateEvent('order:partially-filled', invalidate);
  useRealtimePrivateEvent('order:cancelled', invalidate);
  useRealtimePrivateEvent('trade:executed', invalidate);
}
